<script setup lang="ts">
    import { ref, computed, onMounted, ComputedRef } from 'vue';
    import { getCodeLinkByB64Id, getLibraryLink } from '@/shared/helpers/routing';
    import { getReferenceCodeLabel } from '@/shared/helpers/referableCode.ts';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import UriElement from '@/shared/components/UriElement.vue';
    import NameWithEmail from './NameWithEmail.vue';
    import { getImageDataURL } from '@/shared/helpers/svgHelpers';
    import { longDate, shortDate } from '@/formatters.ts';
    import { getRelatedCodes, type RelatedCodeGroup, type CodeEditContextRefCode, type CodeUpdateInfo } from '@/shared/helpers/api';
    import { AttributeDefinition, AttributeType, CodeRefDisplayMode } from '@/shared/helpers/api/types/entities';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import { CodeResponse } from '@/shared/helpers/api/types/codeEditContextResponse.ts';
    import VueMarkdown from '@/shared/components/VueMarkdown.vue';

    const props = withDefaults(defineProps<{
        code: CodeResponse;
        codeSetName?: string;
        attributeDefinitions: AttributeDefinition[];
        referableCodes: Record<string, CodeEditContextRefCode[]>;
        codeUpdateInfo: CodeUpdateInfo;
        showUserInfo?: boolean;
    }>(), {
        codeSetName: '',
        showUserInfo: false
    });

    const { showError } = useShow();

    const truncLimit = 50;
    const relatedGroups = ref<RelatedCodeGroup[]>([]);
    const showEmptyValues = ref(false);

    type AttributeWithValue = {
        name: string;
        description: string | null;
        type: AttributeType;
        displayAs: string | null;
        referenceDisplayMode: CodeRefDisplayMode;
        isMarkdown: boolean;

        hasValue: boolean;
        displayValue: string;
        referenceCodeId: number;
        referenceCodeIdentity: string;
    };

    const attributes: ComputedRef<AttributeWithValue[]> = computed(() => {
        if (!props.attributeDefinitions) return [];
        return [...props.attributeDefinitions]
            .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
            .map((attr) => {
                const codeAttribute = props.code.attributes.find(ca => ca.definitionName === attr.name);
                return {
                    name: attr.name,
                    description: attr.description,
                    type: attr.attributeType,
                    displayAs: attr.displayAs,
                    referenceDisplayMode: attr.referenceDisplayMode,
                    isMarkdown: attr.isMarkdown,

                    hasValue: codeAttribute !== undefined,
                    displayValue: codeAttribute?.displayValue ?? '',
                    referenceCodeId: codeAttribute?.referenceCode?.id ?? 0,
                    referenceCodeIdentity: codeAttribute?.referenceCode?.identity ?? ''
                };
            });
    });

    const visibleAttributeDefinitions = computed(() => {
        return attributes.value.filter(attr => showEmptyValues.value || attr.hasValue);
    });

    const sortedRelatedGroups = computed(() => {
        return [...relatedGroups.value]
            .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))
            .map(group => ({
                ...group,
                codes: [...group.codes].sort((a, b) => (a.name).localeCompare(b.name))
            }));
    });

    const visibleRelatedGroups = computed(() => {
        return showEmptyValues.value
            ? sortedRelatedGroups.value
            : sortedRelatedGroups.value.filter(group => group.codes.length > 0);
    });

    function getAttributeUpdateInfo(attribute: string) {
        return props.codeUpdateInfo.attributes
            .find(element => element.attributeName === attribute);
    }

    function getAttributeDateCreated(attribute: string): string | null {
        return getAttributeUpdateInfo(attribute)?.dateCreated ?? null;
    }

    function getAttributeDateUpdated(attribute: string): string | null {
        return getAttributeUpdateInfo(attribute)?.dateUpdated ?? null;
    }

    function rowColour(index: number): string {
        return index % 2 === 0 ? 'has-background-white' : 'has-background-white-bis';
    }

    function getReferableCodeLabel(attr: AttributeWithValue) {
        const referableCode = props.referableCodes[attr.name]?.find(rc => rc.identity === attr.referenceCodeIdentity);
        if (!referableCode) {
            return undefined;
        } else {
            return getReferenceCodeLabel(attr.referenceDisplayMode, referableCode);
        }
    }

    onMounted(async () => {
        try {
            relatedGroups.value = await getRelatedCodes(http, props.codeSetName, props.code.identity);
        } catch (error) {
            showError(error);
            relatedGroups.value = [];
        }
    });
</script>

<template>
    <div class="level">
        <div class="level-left">
            <h2 class="title is-5">
                Attribute Details
            </h2>
        </div>
        <div class="level-right">
            <b-checkbox v-model="showEmptyValues">
                Show empty values
            </b-checkbox>
        </div>
    </div>
    <table
        v-if="attributeDefinitions && attributeDefinitions.length > 0"
        class="table is-narrow is-fullwidth">
        <thead>
            <tr>
                <th>Attribute</th>
                <th>Value</th>
                <template v-if="!showUserInfo">
                    <th>
                        Description
                    </th>
                </template>
                <template v-else>
                    <th>
                        Date created
                    </th>
                    <th>
                        Created by
                    </th>
                    <th>
                        Date updated
                    </th>
                    <th>
                        Updated by
                    </th>
                </template>
            </tr>
        </thead>
        <tbody>
            <tr
                v-for="(attr, index) in visibleAttributeDefinitions"
                :key="attr.name"
                :class="rowColour(index)">
                <td>{{ attr.displayAs }}</td>

                <template v-if="attr.hasValue">
                    <td v-if="attr.type === 'CodeRef'">
                        <router-link
                            :to="getCodeLinkByB64Id(encodeIdBase64('Code', attr.referenceCodeId))">
                            {{ getReferableCodeLabel(attr) }}
                        </router-link>
                    </td>
                    <td v-else-if="attr.type === 'LibraryRef'">
                        <router-link
                            :to="getLibraryLink(attr.displayValue)">
                            {{ attr.displayValue }}
                        </router-link>
                    </td>
                    <td v-else-if="attr.type === 'Uri'">
                        <uri-element
                            :value="attr.displayValue"
                            :truncate-at="truncLimit" />
                    </td>
                    <td v-else-if="attr.type === 'Svg'">
                        <img
                            :src="getImageDataURL(attr.displayValue, 100, 200)"
                            alt="SVG attribute value">
                    </td>
                    <td v-else>
                        <div
                            v-if="attr.isMarkdown"
                            class="content markdown-value string-break">
                            <vue-markdown
                                :source="attr.displayValue"
                                :html-enabled="false" />
                        </div>
                        <p
                            v-else
                            class="string-break keep-spaces"
                            v-text="attr.displayValue" />
                    </td>
                </template>
                <template v-else>
                    <td>
                        <span class="has-text-grey is-italic">No value</span>
                    </td>
                </template>

                <template v-if="!showUserInfo">
                    <td>
                        <p class="string-break">
                            {{ attr.description }}
                        </p>
                    </td>
                </template>
                <template v-else>
                    <td>
                        <b-tooltip
                            v-if="attr.hasValue"
                            :label="longDate(getAttributeDateCreated(attr.name)) + ' UTC'"
                            position="is-right">
                            {{ shortDate(getAttributeDateCreated(attr.name)) }}
                        </b-tooltip>
                    </td>
                    <td>
                        <name-with-email
                            v-if="attr.hasValue"
                            :user="getAttributeUpdateInfo(attr.name)?.createdBy" />
                    </td>
                    <td>
                        <b-tooltip
                            v-if="attr.hasValue"
                            :label="longDate(getAttributeDateUpdated(attr.name)) + ' UTC'"
                            position="is-right">
                            {{ shortDate(getAttributeDateUpdated(attr.name)) }}
                        </b-tooltip>
                    </td>
                    <td>
                        <name-with-email
                            v-if="attr.hasValue"
                            :user="getAttributeUpdateInfo(attr.name)?.updatedBy" />
                    </td>
                </template>
            </tr>
            <template
                v-for="(group, index) in visibleRelatedGroups"
                :key="`${index}-${group.label ?? 'no-label'}`">
                <tr
                    v-for="(item, itemIndex) in group.codes"
                    :key="`${index}-${group.label ?? 'no-label'}-${item.id}`"
                    :class="rowColour(visibleAttributeDefinitions.length + index)">
                    <td
                        v-if="itemIndex === 0"
                        :rowspan="group.codes.length"
                        :class="{ 'last-group-label': index === visibleRelatedGroups.length - 1 }">
                        <span>{{ group.label || 'No label' }}</span>
                    </td>
                    <td>
                        <router-link :to="getCodeLinkByB64Id(encodeIdBase64('Code', item.id))">
                            {{ getReferenceCodeLabel(group.referenceDisplayMode, item) }}
                        </router-link>
                    </td>
                    <td
                        v-if="!showUserInfo && itemIndex === 0"
                        :rowspan="group.codes.length"
                        :class="{ 'last-group-label': index === visibleRelatedGroups.length - 1 }">
                        <p class="string-break">
                            {{ group.description }}
                        </p>
                    </td>
                    <td v-if="showUserInfo" />
                    <td v-if="showUserInfo" />
                    <td v-if="showUserInfo" />
                    <td v-if="showUserInfo" />
                </tr>
                <tr
                    v-if="group.codes.length === 0"
                    :key="`${index}-${group.label ?? 'no-label'}-empty`"
                    :class="rowColour(visibleAttributeDefinitions.length + index)">
                    <td :class="{ 'last-group-label': index === visibleRelatedGroups.length - 1 }">
                        <span>{{ group.label || 'No label' }}</span>
                    </td>
                    <td>
                        <span class="has-text-grey is-italic">No items</span>
                    </td>
                    <td
                        v-if="!showUserInfo"
                        :class="{ 'last-group-label': index === visibleRelatedGroups.length - 1 }">
                        <p class="string-break">
                            {{ group.description }}
                        </p>
                    </td>
                    <td v-if="showUserInfo" />
                    <td v-if="showUserInfo" />
                    <td v-if="showUserInfo" />
                    <td v-if="showUserInfo" />
                </tr>
            </template>
        </tbody>
    </table>
    <div
        v-else
        class="has-text-grey has-text-centered">
        No Attributes
    </div>
</template>

<style scoped>
  .string-break {
    word-break: break-word;
  }
  .keep-spaces {
    white-space: pre-wrap;
  }
  .markdown-value {
    border: 1px solid var(--eq-autumn-storm-2);
    border-radius: 4px;
    padding: 0.5rem 0.75rem;
    background-color: var(--eq-bg-highlight);
  }
  td.last-group-label {
    border-bottom: 0;
  }
</style>
